import type { Table } from 'dexie'
import { makeAutoObservable } from 'mobx'

import type WorkflowStore from '@src/service/WorkflowStore'
import type { AnalyticsStore } from '@src/service/analytics'
import type { IFlagsService } from '@src/service/feature-flags'
import type WorkflowDefinitionModel from '@src/service/model/workflow/WorkflowDefinitionModel'
import type { RawWorkflowDefinition } from '@src/service/model/workflow/WorkflowDefinitionModel'
import type StorageService from '@src/service/storage/StorageService'
import type Transport from '@src/service/transport'
import type {
  AgentResource,
  AgentResourceInput,
  AgentResourceUpdateInput,
} from '@src/service/transport/AiClient'
import type {
  AiCustomJobDefinitionV1CreateInput,
  AiCustomJobDefinitionV1UpdateInput,
  AiTriggerDefinitionCustomJobV1,
  CodableAiCustomJobV1,
} from '@src/service/transport/ai-client/aiCustomJobDefinitionV1Schema'
import type {
  AiJobDefinitionByIdInput,
  AiJobDefinitionListInput,
  CodableAiJobDefinition,
} from '@src/service/transport/ai-client/aiJobDefinitionSchema'
import type {
  AiTriggerDefinition,
  AiTriggerDefinitionByIdInput,
  AiTriggerDefinitionCreateInput,
  AiTriggerDefinitionDeleteInput,
  AiTriggerDefinitionListInput,
  AiTriggerDefinitionUpdateInput,
  AiTriggerDefinitionValidateExistingInput,
  AiTriggerDefinitionValidateInput,
  CodableAiTriggerDefinition,
} from '@src/service/transport/ai-client/aiTriggerDefinitionSchema'
import type { AiTriggerDefinitionId } from '@src/service/transport/ai-client/idSchemas'
import { isAiTriggerDefinitionId } from '@src/service/transport/ai-client/idSchemas'
import { AI_JOB_DEFINITION_TABLE_NAME } from '@src/service/worker/repository/AiJobDefinitionRepository'
import { AI_TRIGGER_DEFINITION_TABLE_NAME } from '@src/service/worker/repository/AiTriggerDefinitionRepository'

type UpdateResourceAccessProps = {
  isAllowed: boolean
  resource: AgentResource
  triggerDefinitionId: AiTriggerDefinitionId
  workflowDefinitionId: string
  blockId: string
}

export default class AutomationsStore {
  private readonly agentJobDefinitionsTable: Table<CodableAiJobDefinition>
  private readonly aiAgentTriggerDefinitionsTable: Table<CodableAiTriggerDefinition>

  constructor(
    private storage: StorageService,
    private transport: Transport,
    private workflowStore: WorkflowStore,
    private analytics: AnalyticsStore,
    private flags: IFlagsService,
  ) {
    this.agentJobDefinitionsTable = this.storage.database.table(
      AI_JOB_DEFINITION_TABLE_NAME,
    )
    this.aiAgentTriggerDefinitionsTable = this.storage.database.table(
      AI_TRIGGER_DEFINITION_TABLE_NAME,
    )
    this.subscribeToWebSocket()
    makeAutoObservable(
      this,
      {},
      {
        autoBind: true,
      },
    )
  }

  getResources() {
    return this.transport.ai.automations.getResources()
  }

  getResourceById(id: string) {
    return this.transport.ai.automations.getResourceById(id)
  }

  createResourcePage({ title, content, sources, format }: AgentResourceInput) {
    return this.transport.ai.automations.createResourcePage({
      title,
      content,
      sources,
      format,
    })
  }

  updateResourcePage({ id, title, content, sources }: AgentResourceUpdateInput) {
    return this.transport.ai.automations.updateResourcePage({
      id,
      title,
      content,
      sources,
    })
  }

  uploadResource(files: File[]) {
    return this.transport.ai.automations.uploadResource(files)
  }

  deleteResource(id: string) {
    return this.transport.ai.automations.deleteResource(id)
  }

  /**
   * @deprecated
   * TODO: Remove this once we have migrated all usages to agentDefinitions
   * and the feature flag is 100% rollout for Custom Jobs V1
   * https://linear.app/openphone/issue/PRODU-1183/remove-feature-flag-webaiagentcustomjobsv1
   */
  async getTriggerDefinitions(input: AiTriggerDefinitionListInput, signal?: AbortSignal) {
    const localTriggerDefinitions = await this.aiAgentTriggerDefinitionsTable.toArray()
    if (this.flags.flags.webAiAgentCustomJobsV1) {
      const agentDefinitions = await this.transport.ai.agentDefinitions.getAll(
        input,
        signal,
      )
      const triggerDefinitions = [...agentDefinitions.data, ...localTriggerDefinitions]
      await this.aiAgentTriggerDefinitionsTable.bulkPut(triggerDefinitions)
      return triggerDefinitions
    }

    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const response = await this.transport.ai.triggerDefinitions.getAll(input, signal)
    const triggerDefinitions = [...response.data, ...localTriggerDefinitions]
    await this.aiAgentTriggerDefinitionsTable.bulkPut(triggerDefinitions)
    return triggerDefinitions
  }

  /**
   * @deprecated
   * TODO: Remove this once we have migrated all usages to agentDefinitions
   * and the feature flag is 100% rollout for Custom Jobs V1
   * https://linear.app/openphone/issue/PRODU-1183/remove-feature-flag-webaiagentcustomjobsv1
   */
  async getTriggerDefinitionById(
    input: AiTriggerDefinitionByIdInput,
    signal?: AbortSignal,
  ) {
    if (this.flags.flags.webAiAgentCustomJobsV1) {
      const agentDefinition = await this.transport.ai.agentDefinitions.getById(
        input,
        signal,
      )
      await this.aiAgentTriggerDefinitionsTable.put(agentDefinition)
      return agentDefinition
    }

    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const triggerDefinition = await this.transport.ai.triggerDefinitions.getById(
      input,
      signal,
    )
    await this.aiAgentTriggerDefinitionsTable.put(triggerDefinition)
    return triggerDefinition
  }

  /**
   * @deprecated
   * TODO: Remove this once we have migrated all usages to agentDefinitions
   * and the feature flag is 100% rollout for Custom Jobs V1
   * https://linear.app/openphone/issue/PRODU-1183/remove-feature-flag-webaiagentcustomjobsv1
   */
  async createTriggerDefinition(
    input: AiTriggerDefinitionCreateInput,
    signal?: AbortSignal,
  ) {
    if (this.flags.flags.webAiAgentCustomJobsV1) {
      const agentDefinition = await this.transport.ai.agentDefinitions.create(
        input,
        signal,
      )
      await this.aiAgentTriggerDefinitionsTable.put(agentDefinition)
      return agentDefinition
    }

    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const triggerDefinition = await this.transport.ai.triggerDefinitions.create(
      input,
      signal,
    )
    await this.aiAgentTriggerDefinitionsTable.put(triggerDefinition)
    return triggerDefinition
  }

  async updateLocalTriggerDefinition(
    triggerDefinitionId: string,
    workflowDefinitionId: string,
    stepId: string,
    update: Partial<CodableAiTriggerDefinition>,
  ) {
    const triggerDefinition =
      await this.aiAgentTriggerDefinitionsTable.get(triggerDefinitionId)

    if (!triggerDefinition) {
      return
    }

    const updatedTriggerDefinition = {
      ...triggerDefinition,
      ...update,
    }

    await this.aiAgentTriggerDefinitionsTable.put(updatedTriggerDefinition)

    // Update the step to put the workflow in the draft/unpublished changes state
    this.workflowStore.updateNode({
      definitionId: workflowDefinitionId,
      stepId,
      update: {},
      skipAddingToHistory: true,
    })
  }

  /**
   * @deprecated
   * TODO: Remove this once we have migrated all usages to agentDefinitions
   * and the feature flag is 100% rollout for Custom Jobs V1
   * https://linear.app/openphone/issue/PRODU-1183/remove-feature-flag-webaiagentcustomjobsv1
   */
  deleteRemovedAiAgentTriggerDefinitionsFromRemote(
    definition: WorkflowDefinitionModel,
    definitionBeforeModifications: RawWorkflowDefinition,
  ) {
    Object.values(definitionBeforeModifications.workflowSteps)
      .filter((step) => step.definitionId === 'WSDvoiceAgent')
      .forEach((step) => {
        if (definition.workflowSteps[step.id]) {
          return
        }

        const triggerDefinitionId = step.configuration?.find(
          (c) => c.variableKey === 'WVagentTriggerDefinitionId',
        )?.value

        if (!isAiTriggerDefinitionId(triggerDefinitionId)) {
          return
        }

        void this.transport.ai.agentDefinitions.delete({
          id: triggerDefinitionId,
        })

        void this.agentJobDefinitionsTable.delete(triggerDefinitionId)
      })
  }

  // Discard local Ai Agent trigger definitions for a workflow
  discardLocalAiAgentTriggerDefinitions(
    workflowSteps: WorkflowDefinitionModel['workflowSteps'],
  ) {
    const triggerDefinitionIds = Object.values(workflowSteps)
      .map((step) => {
        const triggerDefinitionId = step?.configuration?.find(
          (config) => config.variableKey === 'WVagentTriggerDefinitionId',
        )?.value as AiTriggerDefinitionId

        return triggerDefinitionId
      })
      .filter(Boolean)

    void Promise.all(
      triggerDefinitionIds.map((triggerId) =>
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        this.discardLocalTriggerDefinition(triggerId),
      ),
    )
  }

  /**
   * @deprecated
   * TODO: Remove this once we have migrated all usages to agentDefinitions
   * and the feature flag is 100% rollout for Custom Jobs V1
   * https://linear.app/openphone/issue/PRODU-1183/remove-feature-flag-webaiagentcustomjobsv1
   */
  async discardLocalTriggerDefinition(triggerDefinitionId: AiTriggerDefinitionId) {
    let remoteAgentDefinition: AiTriggerDefinition
    if (this.flags.flags.webAiAgentCustomJobsV1) {
      remoteAgentDefinition = await this.transport.ai.agentDefinitions.getById({
        id: triggerDefinitionId,
      })
    } else {
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      remoteAgentDefinition = await this.transport.ai.triggerDefinitions.getById({
        id: triggerDefinitionId,
      })
    }

    if (!remoteAgentDefinition) {
      // delete the local trigger definition
      await this.aiAgentTriggerDefinitionsTable.delete(triggerDefinitionId)
      return
    }

    // update the local trigger definition with the remote one
    await this.aiAgentTriggerDefinitionsTable.put(remoteAgentDefinition)
  }

  /**
   * @deprecated
   * TODO: Remove this once we have migrated all usages to agentDefinitions
   * and the feature flag is 100% rollout for Custom Jobs V1
   * https://linear.app/openphone/issue/PRODU-1183/remove-feature-flag-webaiagentcustomjobsv1
   */
  async updateTriggerDefinition(
    input: AiTriggerDefinitionUpdateInput,
    signal?: AbortSignal,
  ) {
    if (this.flags.flags.webAiAgentCustomJobsV1) {
      const agentDefinition = await this.transport.ai.agentDefinitions.update(
        input,
        signal,
      )
      await this.aiAgentTriggerDefinitionsTable.put(agentDefinition)
      return agentDefinition
    }

    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const triggerDefinition = await this.transport.ai.triggerDefinitions.update(
      input,
      signal,
    )
    await this.aiAgentTriggerDefinitionsTable.put(triggerDefinition)
    return triggerDefinition
  }

  async updateResourceAccess({
    isAllowed,
    resource,
    triggerDefinitionId,
    workflowDefinitionId,
    blockId,
  }: UpdateResourceAccessProps) {
    const triggerDefinition =
      await this.aiAgentTriggerDefinitionsTable.get(triggerDefinitionId)

    const updatedResources = isAllowed
      ? [{ definitionId: resource.id }, ...(triggerDefinition?.resources || [])]
      : triggerDefinition?.resources.filter((r) => r.definitionId !== resource.id)

    const updatedTriggerDefinition = {
      ...triggerDefinition,
      resources: updatedResources,
    } as CodableAiTriggerDefinition

    // Update the step to put the workflow in the draft/unpublished changes state
    this.workflowStore.updateNode({
      definitionId: workflowDefinitionId,
      stepId: blockId,
      update: {},
      skipAddingToHistory: true,
    })

    if (!isAllowed) {
      this.analytics.aiAutomations.removeKnowledgePageAccess(resource.title)
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- FIXME: Fix this ESLint violation!
    return this.aiAgentTriggerDefinitionsTable.put(updatedTriggerDefinition)
  }

  async addResourceToTriggerDefinition({
    resourceId,
    triggerDefinitionId,
    workflowDefinitionId,
    blockId,
  }: {
    resourceId: AgentResource['id']
    triggerDefinitionId: AiTriggerDefinitionId
    workflowDefinitionId: string
    blockId: string
  }) {
    const triggerDefinition =
      await this.aiAgentTriggerDefinitionsTable.get(triggerDefinitionId)

    if (!triggerDefinition) {
      throw new Error('Trigger definition not found')
    }

    // Check if the resource already exists to avoid duplicates
    const resourceIdAlreadyExists = triggerDefinition.resources.some(
      (r) => r.definitionId === resourceId,
    )

    if (resourceIdAlreadyExists) {
      return triggerDefinition
    }

    const updatedTriggerDefinition = {
      ...triggerDefinition,
      resources: [
        {
          definitionId: resourceId,
        },
        ...triggerDefinition.resources,
      ],
    } as CodableAiTriggerDefinition

    // Update the step to put the workflow in the draft/unpublished changes state
    this.workflowStore.updateNode({
      definitionId: workflowDefinitionId,
      stepId: blockId,
      update: {},
      skipAddingToHistory: true,
    })

    void this.aiAgentTriggerDefinitionsTable.put(updatedTriggerDefinition)
    return updatedTriggerDefinition
  }

  async deleteTriggerDefinition(
    input: AiTriggerDefinitionDeleteInput,
    signal?: AbortSignal,
  ) {
    await this.transport.ai.agentDefinitions.delete(input, signal)
  }

  /**
   * @deprecated
   * TODO: Remove this once we have migrated all usages to agentDefinitions
   * and the feature flag is 100% rollout for Custom Jobs V1
   * https://linear.app/openphone/issue/PRODU-1183/remove-feature-flag-webaiagentcustomjobsv1
   */
  validateTriggerDefinition(
    input: AiTriggerDefinitionValidateInput,
    signal?: AbortSignal,
  ) {
    if (this.flags.flags.webAiAgentCustomJobsV1) {
      return this.transport.ai.agentDefinitions.validate(input, signal)
    }

    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return this.transport.ai.triggerDefinitions.validate(input, signal)
  }

  /**
   * @deprecated
   * TODO: Remove this once we have migrated all usages to agentDefinitions
   * and the feature flag is 100% rollout for Custom Jobs V1
   * https://linear.app/openphone/issue/PRODU-1183/remove-feature-flag-webaiagentcustomjobsv1
   */
  validateExistingTriggerDefinition(
    input: AiTriggerDefinitionValidateExistingInput,
    signal?: AbortSignal,
  ) {
    if (this.flags.flags.webAiAgentCustomJobsV1) {
      return this.transport.ai.agentDefinitions.validateExisting(input, signal)
    }

    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return this.transport.ai.triggerDefinitions.validateExisting(input, signal)
  }

  getCustomJobDefinitionsV1() {
    return this.transport.ai.customJobsV1.getAll()
  }

  createCustomJobDefinitionV1(input: AiCustomJobDefinitionV1CreateInput) {
    return this.transport.ai.customJobsV1.create(input)
  }

  updateCustomJobDefinitionV1(update: AiCustomJobDefinitionV1UpdateInput) {
    return this.transport.ai.customJobsV1.update(update)
  }

  deleteCustomJobDefinitionV1(id: string) {
    return this.transport.ai.customJobsV1.delete(id)
  }

  async addCustomJobToLocalTriggerDefinition({
    jobId,
    triggerDefinitionId,
    workflowDefinitionId,
    blockId,
  }: {
    jobId: CodableAiCustomJobV1['id']
    triggerDefinitionId: AiTriggerDefinitionId
    workflowDefinitionId: string
    blockId: string
  }) {
    const triggerDefinition =
      await this.aiAgentTriggerDefinitionsTable.get(triggerDefinitionId)

    if (!triggerDefinition) {
      throw new Error('Trigger definition not found')
    }

    // Check if the job already exists to avoid duplicates
    const jobsFromTriggerDefinition =
      triggerDefinition.jobs as AiTriggerDefinitionCustomJobV1[]
    const jobIdAlreadyExists = jobsFromTriggerDefinition.some(
      (j) => j.definitionId === jobId,
    )

    if (jobIdAlreadyExists) {
      return triggerDefinition
    }

    const updatedTriggerDefinition = {
      ...triggerDefinition,
      jobs: [
        {
          definitionId: jobId,
        },
        ...jobsFromTriggerDefinition,
      ],
    }

    // Update the step to put the workflow in the draft/unpublished changes state
    this.workflowStore.updateNode({
      definitionId: workflowDefinitionId,
      stepId: blockId,
      update: {},
      skipAddingToHistory: true,
    })

    void this.aiAgentTriggerDefinitionsTable.put(updatedTriggerDefinition)
    return updatedTriggerDefinition
  }

  async getJobDefinitions(input?: AiJobDefinitionListInput, signal?: AbortSignal) {
    return this.transport.ai.jobDefinitions.getAll(input, signal).then((response) => {
      void this.agentJobDefinitionsTable.bulkPut(response.data)
      return response.data
    })
  }

  async getJobDefinitionById(input: AiJobDefinitionByIdInput, signal?: AbortSignal) {
    const jobDefinition = await this.transport.ai.jobDefinitions.getById(input, signal)
    if (jobDefinition) {
      void this.agentJobDefinitionsTable.put(jobDefinition)
    }
    return jobDefinition
  }

  /**
   * Count the number of call flows (inboxes) in which the specified resource is attached.
   *
   * @param currentEntityId - The ID of the current phone number.
   * @param resourceId - The identifier of the resource to check.
   * @returns The number of call flows that have the resource attached.
   */
  async getResourceUsageCountInCallflows(currentEntityId: string, resourceId: string) {
    const entityIdsChecked = new Set<string>()

    const count = await this.aiAgentTriggerDefinitionsTable
      .filter((triggerDefinition) => {
        const hasResource = triggerDefinition.resources.some(
          (r) => r.definitionId === resourceId,
        )

        if (hasResource && !entityIdsChecked.has(triggerDefinition.entityId)) {
          entityIdsChecked.add(triggerDefinition.entityId)
          return true
        }

        return false
      })
      .count()

    if (currentEntityId && entityIdsChecked.has(currentEntityId)) {
      return count - 1
    }
    return count
  }

  get localTriggerDefinitions() {
    return this.aiAgentTriggerDefinitionsTable.toArray()
  }

  async getLocalTriggerDefinitionsById() {
    const localDefinitions = await this.localTriggerDefinitions
    return localDefinitions.reduce(
      (acc, triggerDefinition) => {
        acc[triggerDefinition.id] = triggerDefinition
        return acc
      },
      {} as Record<AiTriggerDefinitionId, CodableAiTriggerDefinition>,
    )
  }

  get localJobDefinitions() {
    return this.agentJobDefinitionsTable.toArray()
  }

  subscribeToWebSocket() {
    this.transport.onNotificationData.subscribe((event) => {
      switch (event.type) {
        case 'agent-definition-create':
          void this.aiAgentTriggerDefinitionsTable.put(event.definition)
          return
        case 'agent-definition-update':
          void this.aiAgentTriggerDefinitionsTable.put(event.definition)
          return
        case 'agent-definition-delete':
          void this.aiAgentTriggerDefinitionsTable.delete(event.id)
          return
      }
    })
  }
}
